import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import "./chat.css";
import firebase from "../../firebase-config";
import { firestoreService } from "../../services/firestoreService";
import { proxyService } from "../../services/proxyService";
import { trackEvent } from "../../config/googleAnalytics";

const ERROR_MESSAGES = {
  'Cannot connect to server': 'Cannot connect to chat server. Please try again later.',
  'No token provided': 'Authentication error. Please try logging in again.',
  'Invalid token': 'Your session has expired. Please log in again.',
  'No active subscription': 'No active subscription found. Please check your subscription status.',
  'auth/network-request-failed': 'Network error. Please check your internet connection and try again.',
};

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Direct URL to VectorShift chatbot
const VECTORSHIFT_CHATBOT_URL = 'https://app.vectorshift.ai/chatbots/deployed/6804bcf34ba5bf31debe824b';

export const ChatInterface = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chatContainerRef = useRef(null);

  // Set viewport meta tag for better mobile display
  useEffect(() => {
    // Find or create viewport meta tag
    let viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) {
      viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      document.head.appendChild(viewportMeta);
    }
    
    // Set viewport for better mobile display without scrolling
    viewportMeta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover';
    
    return () => {
      // Reset to default when component unmounts
      viewportMeta.content = 'width=device-width, initial-scale=1.0';
    };
  }, []);

  const handleLogout = () => {
    // Track logout from chat page
    trackEvent('User', 'Logout', 'From Chat Page');
    
    firebase.auth.signOut()
      .then(() => {
        localStorage.removeItem('userSubscription');
        navigate('/login');
      })
      .catch((error) => {
        console.error('Error signing out:', error);
      });
  };

  const getTokenWithRetry = async (retries = 0) => {
    try {
      const user = firebase.auth.currentUser;
      if (!user) {
        throw new Error('No user found');
      }
      return await user.getIdToken(true);
    } catch (error) {
      if (retries < MAX_RETRIES) {
        console.log(`Retrying token refresh (${retries + 1}/${MAX_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return getTokenWithRetry(retries + 1);
      }
      throw error;
    }
  };

  useEffect(() => {
    const checkSubscriptionAndLoadChat = async () => {
      try {
        const user = firebase.auth.currentUser;
        if (!user) {
          console.log('No user found, redirecting to login');
          trackEvent('Error', 'Chat Access', 'No user found');
          navigate('/login');
          return;
        }

        // Get fresh token with retry logic
        const token = await getTokenWithRetry();
        console.log('Got fresh token:', token.substring(0, 10) + '...');

        // Try to get subscription from Firestore directly instead of using the server API
        let subscription = await firestoreService.getSubscription(user.uid);
        
        // If no subscription in Firestore, check localStorage as fallback
        if (!subscription || !subscription.isActive) {
          const storedSubscription = localStorage.getItem('userSubscription');
          if (storedSubscription) {
            subscription = JSON.parse(storedSubscription);
          }
        }
        
        console.log('Subscription status:', subscription);
        
        // If still no active subscription, redirect to pricing
        if (!subscription || !subscription.isActive) {
          trackEvent('Error', 'Chat Access', 'No active subscription');
          navigate('/pricing');
          return;
        }

        // Check if subscription has expired
        const expiryDate = new Date(subscription.expiryDate);
        if (expiryDate <= new Date()) {
          localStorage.removeItem('userSubscription');
          alert('Your subscription has expired. Please renew to continue using RizzologyGPT.');
          trackEvent('Error', 'Chat Access', 'Subscription expired');
          navigate('/pricing');
          return;
        }
        
        // Track successful subscription verification
        trackEvent('Chat', 'Subscription Verified', subscription.type);

        // Generate a session ID directly without server
        const sessionId = `user_${user.uid.substring(0, 8)}_${Date.now()}`;
        const vectorShiftUrl = `${VECTORSHIFT_CHATBOT_URL}?session=${sessionId}`;
        
        console.log('Created direct VectorShift session:', sessionId);
        console.log('VectorShift URL:', vectorShiftUrl);
        
        // Track session creation
        trackEvent('Chat', 'Session Created', sessionId);
        
        // Clear any existing content
        if (chatContainerRef.current) {
          chatContainerRef.current.innerHTML = '';
          
          // Create and append the iframe
          const chatFrame = document.createElement('iframe');
          // Add sandbox attribute with needed permissions
          chatFrame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox');
          chatFrame.src = vectorShiftUrl;
          chatFrame.style.width = '100%';
          chatFrame.style.height = '100%';
          chatFrame.style.border = 'none';
          chatFrame.style.position = 'absolute';
          chatFrame.style.top = '0';
          chatFrame.style.left = '0';
          chatFrame.style.right = '0';
          chatFrame.style.bottom = '0';
          chatFrame.style.margin = '0';
          chatFrame.style.padding = '0';
          chatFrame.style.overflow = 'hidden';
          
          // Add mobile optimization attributes
          chatFrame.setAttribute('scrolling', 'no');
          chatFrame.setAttribute('frameborder', '0');
          
          // Add content-specific attributes for mobile
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // Adding a class for mobile-specific styling
            chatFrame.classList.add('mobile-chat-frame');
            
            // Track mobile usage
            trackEvent('Device', 'Mobile Chat', window.innerWidth.toString());
            
            // Attempt to ensure the iframe content fits the viewport
            const resizeObserver = new ResizeObserver(() => {
              try {
                // Force iframe content to match its container size
                if (chatFrame.contentWindow && chatFrame.contentWindow.document && chatFrame.contentWindow.document.body) {
                  const style = chatFrame.contentWindow.document.createElement('style');
                  style.textContent = `
                    html, body { 
                      height: 100% !important; 
                      width: 100% !important; 
                      overflow: hidden !important;
                      position: fixed !important;
                      top: 0 !important;
                      left: 0 !important;
                    }
                  `;
                  chatFrame.contentWindow.document.head.appendChild(style);
                }
              } catch (e) {
                console.log('Could not modify iframe content - cross-origin restriction');
              }
            });
            
            // Start observing the iframe
            resizeObserver.observe(chatFrame);
          } else {
            // Track desktop usage
            trackEvent('Device', 'Desktop Chat', window.innerWidth.toString());
          }
          
          // Let App.js handle the body styles for fullscreen
          // This prevents affecting other pages' scrolling
          
          chatContainerRef.current.appendChild(chatFrame);
        }
        
        setLoading(false);
        
      } catch (error) {
        console.error('Error accessing chat:', error);
        const errorMessage = ERROR_MESSAGES[error.code] || ERROR_MESSAGES[error.message] || 'Failed to load chat interface';
        setError(errorMessage);
        setLoading(false);
        
        // Track error
        trackEvent('Error', 'Chat Interface', error.message || error.code || 'Unknown error');
        
        if (error.message === 'No user found' || error.message === 'No token provided' || error.message === 'Invalid token' || error.code === 'auth/network-request-failed') {
          // Wait a bit before redirecting to give user time to see the error
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else if (error.message === 'No active subscription') {
          navigate('/pricing');
        }
      }
    };

    checkSubscriptionAndLoadChat();
  }, [navigate]);

  return (
    <div className="chat-page">
      <div className="chat-header">
        <h1>Rizzology-GPT</h1>
        <button className="logout-button" onClick={handleLogout}>Logout</button>
      </div>
      {loading && (
        <div className="loading-compact">
          <div className="loading-spinner"></div>
          <div>Loading chat...</div>
        </div>
      )}
      {error && (
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      )}
      <div ref={chatContainerRef} className="chat-container"></div>
    </div>
  );
};

export default ChatInterface;
  